// services/testHarness.js
// שכבת בדיקות — window.__MADAD_TEST__
//
// פעיל רק כש-REACT_APP_TEST_MODE=true
// בproduction — הקוד לא נכנס ל-build בכלל
//
// מטרה: לאפשר ל-AI חיצוני (שלא כתב את ה-Frontend) לכתוב סקריפט בדיקה
// שמדמה אינטראקציית משתמש ומוודא שהקריאות ל-Backend תואמות את ה-API Documentation
//
// ★ כל אלמנט אינטראקטיבי חייב data-testid בפורמט:
//   {page}__{element-type}--{identifier}
//   דוגמאות: login__button--submit, dashboard__card--company-score
//
// ═══════════════════════════════════════════════════════════════
// ERROR CODES — [TH-XXX]
// ═══════════════════════════════════════════════════════════════
// 100-199  API / Network
//   101    API call timeout (waitForApiCall)
//
// 200-299  Forms
//   201    fillForm: invalid arguments (bad signature)
//   202    fillForm: element not found by testId
//   203    fillForm: no inner input inside wrapper element
//   204    fillForm: unsupported element tagName
//   210    submitForm: form not found by testId
//   211    submitForm: no submit button, using fallback event
//   220    clearForm: form not found by testId
//
// 300-399  Navigation
//   301    navigate: no _navigateRef set
//   310    waitForNavigation: timeout
//   320    navigateAndWait: timeout on element or path
//
// 400-499  DOM / Interactions
//   401    clickButton: element not found
//   410    clickTableRow: table not found
//   411    clickTableRow: row index out of range
//   420    clickTableAction: table not found
//   421    clickTableAction: row out of range
//   422    clickTableAction: action not found in row
//   430    selectDropdown: element not found
//   431    selectDropdown: option not found
//   432    selectDropdown: could not interact with dropdown
//   440    toggleCheckbox: element not found
//   450    setSlider: element not found
//   460    waitForElement: timeout
//
// 500-599  State
//   (reserved)
//
// 600-699  Auth / Shortcuts
//   601    login: 2FA field did not appear after 403
//   602    login: 2FA required but no password2 provided
//   610    logout: button not found
// ═══════════════════════════════════════════════════════════════

import { getApiLog, getLastApiCall, clearApiLog, waitForApiCall } from './api';

// =============================================================================
// Error helper — logs with code + returns error object
// =============================================================================
function _err(code, func, detail, extra = {}) {
  const msg = `[TH-${code}] ${func}: ${detail}`;
  console.warn(msg, extra);
  return { success: false, error: msg, code };
}

// =============================================================================
// Navigation references — ירשמו על ידי App.js
// =============================================================================
let _navigateRef = null;
let _authData = { user: null, token: null };
let _notificationData = { unreadCount: 0 };
let _companyData = null;

export function setNavigateRef(navigateFn) {
  _navigateRef = navigateFn;
}

export function setTestAuthData(user, token) {
  _authData = { user, token: token ? token.substring(0, 10) + '...' : null };
}

export function setTestNotificationData(unreadCount) {
  _notificationData = { unreadCount };
}

export function setTestCompanyData(company) {
  _companyData = company;
}

// =============================================================================
// Helper: מחכה ל-DOM update
// =============================================================================
function _tick(ms = 50) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// =============================================================================
// Helper: מחפש אלמנט לפי data-testid
// =============================================================================
function _findByTestId(testId) {
  return document.querySelector(`[data-testid="${testId}"]`);
}

// =============================================================================
// Helper: מדמה event של React על input
// React שומרת state ב-fiber ולא מגיבה ל-element.value = x רגיל.
// צריך לקרוא ל-setter דרך הprototype ואז לירות events.
// =============================================================================
function _setNativeValue(element, value) {
  // React 18 tracks input values via an internal _valueTracker on the element.
  // To force React to see the change, we must:
  // 1. Use the native prototype setter (bypasses React's synthetic wrapper)
  // 2. Invalidate React's value tracker so it detects the change
  // 3. Dispatch native 'input' event which React listens to

  // Use element's own document context to handle cross-iframe scenarios
  const win = element.ownerDocument?.defaultView || window;

  try {
    const nativeSetter =
      Object.getOwnPropertyDescriptor(win.HTMLInputElement.prototype, 'value')?.set ||
      Object.getOwnPropertyDescriptor(win.HTMLTextAreaElement.prototype, 'value')?.set ||
      Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), 'value')?.set;

    if (nativeSetter) {
      nativeSetter.call(element, value);
    } else {
      element.value = value;
    }
  } catch (e) {
    // Fallback: direct assignment (works for most cases, React may not detect it
    // without tracker invalidation below, but combined with events it usually works)
    console.warn('[TH] _setNativeValue native setter failed, using fallback:', e.message);
    element.value = value;
  }

  // Invalidate React's value tracker so it sees the change as "new"
  const tracker = element._valueTracker;
  if (tracker) {
    tracker.setValue(value === '' ? ' ' : '');
  }

  // Dispatch events — React 18 listens to native 'input' events on inputs
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

// =============================================================================
// Setup — נקרא מ-App.js רק ב-test mode
// =============================================================================
export function setupTestHarness() {
  // Skip if already initialized
  if (window.__MADAD_TEST__) return;

  window.__MADAD_TEST__ = {
    // =====================================================================
    // API Log — מיובא מ-api.js
    // =====================================================================
    getApiLog,
    getLastApiCall,
    clearApiLog,
    waitForApiCall,

    // =====================================================================
    // Navigation
    // =====================================================================

    /**
     * ניווט לנתיב
     * @param {string} path — למשל '/dashboard', '/admin/users'
     * @returns {boolean} האם הניווט הצליח
     */
    navigate(path) {
      if (_navigateRef) {
        _navigateRef(path);
        return true;
      }
      return false;
    },

    /**
     * מחזיר נתיב נוכחי
     * @returns {string} למשל '/dashboard?unit_id=1'
     */
    getCurrentPath() {
      return window.location.pathname + window.location.search;
    },

    // =====================================================================
    // Forms
    // =====================================================================

    /**
     * ממלא טופס לפי מפתחות data-testid
     * @param {Object} fields — { "login__input--username": "admin", "login__input--password": "pass" }
     * @returns {Object} results — { "testId": { success: true/false, error?: string } }
     */
    async fillForm(fieldsOrFormId, maybeValues) {
      // Support 2 signatures:
      // fillForm({ testId: value, ... })           — original
      // fillForm(formTestId, { testId: value })    — test AI expected signature
      let fields;
      if (typeof fieldsOrFormId === 'string' && maybeValues && typeof maybeValues === 'object') {
        fields = maybeValues;
        console.log('[TH] fillForm: form=%s, fields=%s', fieldsOrFormId, Object.keys(fields));
      } else if (typeof fieldsOrFormId === 'object') {
        fields = fieldsOrFormId;
        console.log('[TH] fillForm:', Object.keys(fields));
      } else {
        return _err(201, 'fillForm', 'invalid arguments — expected (fields) or (formTestId, fields)', { fieldsOrFormId, maybeValues });
      }
      const results = {};
      for (const [testId, value] of Object.entries(fields)) {
        const element = _findByTestId(testId);
        if (!element) {
          results[testId] = _err(202, 'fillForm', `element not found: ${testId}`);
          continue;
        }

        try {
        const tagName = element.tagName.toLowerCase();
        const inputType = element.getAttribute('type')?.toLowerCase();
        console.log(`[TH] fillForm: ${testId} → tag=${tagName}, type=${inputType}`);

        if (tagName === 'input' && inputType === 'checkbox') {
          // Checkbox — toggle אם הערך שונה מהמצב הנוכחי
          if (element.checked !== (value === true || value === 'true')) {
            element.click();
          }
          results[testId] = { success: true };
        } else if (tagName === 'input' || tagName === 'textarea') {
          // Text/number/date/etc input — חפש input בתוך MUI wrapper
          const input = element.querySelector('input, textarea') || element;
          input.focus();
          _setNativeValue(input, value);
          input.blur();
          results[testId] = { success: true };
        } else if (tagName === 'select') {
          element.value = value;
          element.dispatchEvent(new Event('change', { bubbles: true }));
          results[testId] = { success: true };
        } else if (tagName === 'div' || tagName === 'label') {
          // MUI TextField wrapper — חפש input בפנים
          const innerInput = element.querySelector('input, textarea');
          if (innerInput) {
            innerInput.focus();
            _setNativeValue(innerInput, value);
            innerInput.blur();
            results[testId] = { success: true };
          } else {
            results[testId] = _err(203, 'fillForm', `no inner input inside wrapper: ${testId}`);
          }
        } else {
          results[testId] = _err(204, 'fillForm', `unsupported tag: ${tagName}`, { testId });
        }
        } catch (err) {
          console.error(`[TH] fillForm error on ${testId}:`, err.message);
          results[testId] = _err(205, 'fillForm', `exception: ${err.message}`, { testId });
        }

        await _tick(30);
      }
      return results;
    },

    /**
     * לוחץ כפתור submit של טופס
     * @param {string} formTestId — data-testid של הform
     */
    async submitForm(formTestId) {
      console.log('[TH] submitForm:', formTestId);
      const form = _findByTestId(formTestId);
      if (!form) return _err(210, 'submitForm', `form not found: ${formTestId}`);

      // חפש כפתור submit בתוך הform
      const submitBtn = form.querySelector(
        'button[type="submit"], [data-testid*="button--submit"], [data-testid*="button--save"]'
      );
      if (submitBtn) {
        console.log('[TH] submitForm: found button:', submitBtn.tagName, submitBtn.textContent?.substring(0, 20));
        submitBtn.click();
        await _tick(100);
        return { success: true };
      }

      // fallback — נסה submit event
      console.warn('[TH-211] submitForm: no submit button found, using fallback submit event');
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      await _tick(100);
      return { success: true };
    },

    /**
     * מנקה את כל השדות בטופס
     * @param {string} formTestId — data-testid של הform
     */
    async clearForm(formTestId) {
      const form = _findByTestId(formTestId);
      if (!form) return _err(220, 'clearForm', `form not found: ${formTestId}`);

      const inputs = form.querySelectorAll('input, textarea, select');
      inputs.forEach((input) => {
        if (input.type === 'checkbox') {
          if (input.checked) input.click();
        } else {
          _setNativeValue(input, '');
        }
      });
      await _tick(50);
      return { success: true };
    },

    // =====================================================================
    // Interactions
    // =====================================================================

    /**
     * לוחץ על כפתור
     * @param {string} testId — data-testid
     */
    async clickButton(testId) {
      const btn = _findByTestId(testId);
      if (!btn) return _err(401, 'clickButton', `element not found: ${testId}`);
      console.log('[TH] clickButton:', testId, '→', btn.tagName, btn.textContent?.substring(0, 30));
      btn.click();
      await _tick(300);
      return { success: true };
    },

    /**
     * לוחץ על שורה בטבלה
     * @param {string} tableTestId — data-testid של הטבלה
     * @param {number} rowIndex — מספר שורה (0-based)
     */
    async clickTableRow(tableTestId, rowIndex) {
      const table = _findByTestId(tableTestId);
      if (!table) return _err(410, 'clickTableRow', `table not found: ${tableTestId}`);
      const rows = table.querySelectorAll('tbody tr');
      if (rowIndex >= rows.length) {
        return _err(411, 'clickTableRow', `row ${rowIndex} out of range (${rows.length} rows)`);
      }
      rows[rowIndex].click();
      await _tick(100);
      return { success: true };
    },

    /**
     * לוחץ על פעולה בשורת טבלה
     * @param {string} tableTestId — data-testid של הטבלה
     * @param {number} rowIndex — מספר שורה (0-based)
     * @param {string} actionTestId — data-testid של כפתור הפעולה
     */
    async clickTableAction(tableTestId, rowIndex, actionTestId) {
      const table = _findByTestId(tableTestId);
      if (!table) return _err(420, 'clickTableAction', `table not found: ${tableTestId}`);
      const rows = table.querySelectorAll('tbody tr');
      if (rowIndex >= rows.length) {
        return _err(421, 'clickTableAction', `row ${rowIndex} out of range`);
      }
      const action = rows[rowIndex].querySelector(`[data-testid="${actionTestId}"]`);
      if (!action) {
        return _err(422, 'clickTableAction', `action ${actionTestId} not found in row ${rowIndex}`);
      }
      action.click();
      await _tick(100);
      return { success: true };
    },

    /**
     * בוחר ערך ב-MUI Select dropdown
     * @param {string} testId — data-testid של ה-select wrapper
     * @param {string|number} value — ערך לבחירה (data-value או text)
     */
    async selectDropdown(testId, value) {
      const element = _findByTestId(testId);
      if (!element) return _err(430, 'selectDropdown', `element not found: ${testId}`);

      console.log('[TH] selectDropdown:', testId, '→ value:', value, '→ tag:', element.tagName);

      // Strategy 1: MUI Select / TextField select
      // Look for the clickable trigger: [role="combobox"] or .MuiSelect-select or [role="button"]
      const muiTrigger = element.querySelector(
        '[role="combobox"], [role="button"].MuiSelect-select, .MuiSelect-select, .MuiSelect-nativeInput'
      );

      if (muiTrigger) {
        console.log('[TH] selectDropdown: found MUI trigger:', muiTrigger.className?.substring(0, 50));

        // If it's the native hidden input, click the sibling select display
        const clickTarget = muiTrigger.classList?.contains('MuiSelect-nativeInput')
          ? muiTrigger.parentElement?.querySelector('.MuiSelect-select') || muiTrigger
          : muiTrigger;

        clickTarget.click();
        await _tick(300);

        // MUI Select opens a Popover/Menu with [role="listbox"] in a portal
        const listbox = document.querySelector('[role="listbox"]');
        if (listbox) {
          console.log('[TH] selectDropdown: listbox found, options:', listbox.querySelectorAll('[role="option"], .MuiMenuItem-root').length);
          const options = listbox.querySelectorAll('[role="option"], .MuiMenuItem-root');
          for (const option of options) {
            const optValue = option.dataset.value ?? option.getAttribute('data-value');
            const optText = option.textContent?.trim();
            if (optValue === String(value) || optText === String(value)) {
              console.log('[TH] selectDropdown: clicking option:', optValue, optText);
              option.click();
              await _tick(200);
              return { success: true };
            }
          }
          // Close dropdown if option not found
          document.body.click();
          await _tick(50);
          return _err(431, 'selectDropdown', `option "${value}" not found. Available: ${Array.from(options).map(o => o.dataset.value || o.textContent?.trim()).join(', ')}`);
        }

        // Listbox didn't appear — try pressing Enter or mousedown
        console.warn('[TH] selectDropdown: listbox not found after click, trying mousedown');
        clickTarget.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        await _tick(300);

        const listbox2 = document.querySelector('[role="listbox"]');
        if (listbox2) {
          const options = listbox2.querySelectorAll('[role="option"], .MuiMenuItem-root');
          for (const option of options) {
            if (option.dataset.value === String(value) || option.textContent?.trim() === String(value)) {
              option.click();
              await _tick(200);
              return { success: true };
            }
          }
          document.body.click();
          await _tick(50);
          return _err(431, 'selectDropdown', `option "${value}" not found after mousedown`);
        }
      }

      // Strategy 2: Native <select> element
      const nativeSelect = element.tagName?.toLowerCase() === 'select'
        ? element
        : element.querySelector('select');
      if (nativeSelect) {
        console.log('[TH] selectDropdown: using native select');
        _setNativeValue(nativeSelect, value);
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        await _tick(100);
        return { success: true };
      }

      // Strategy 3: data-testid is on the outer wrapper (TextField)
      // Try to find the hidden input and dispatch change directly
      const hiddenInput = element.querySelector('input[type="hidden"], input.MuiSelect-nativeInput');
      if (hiddenInput) {
        console.log('[TH] selectDropdown: found hidden input, dispatching change');
        _setNativeValue(hiddenInput, value);
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        await _tick(100);
        return { success: true };
      }

      return _err(432, 'selectDropdown', `could not interact with dropdown: ${testId}. Element classes: ${element.className?.substring(0, 80)}`);
    },

    /**
     * מחליף מצב checkbox
     * @param {string} testId — data-testid
     * @returns {{ success, checked }}
     */
    async toggleCheckbox(testId) {
      const element = _findByTestId(testId);
      if (!element) return _err(440, 'toggleCheckbox', `element not found: ${testId}`);

      // MUI Checkbox: click על ה-container, או ישירות על ה-input
      const checkbox = element.querySelector('input[type="checkbox"]') || element;
      checkbox.click();
      await _tick(50);
      return { success: true, checked: checkbox.checked };
    },

    /**
     * קובע ערך ב-slider
     * @param {string} testId — data-testid
     * @param {number} value — ערך חדש
     */
    async setSlider(testId, value) {
      const element = _findByTestId(testId);
      if (!element) return _err(450, 'setSlider', `element not found: ${testId}`);

      // MUI Slider: חפש את ה-input[type="range"] בתוך האלמנט
      const slider = element.querySelector('input[type="range"]') || element;
      _setNativeValue(slider, value);
      slider.dispatchEvent(new Event('change', { bubbles: true }));
      await _tick(100);
      return { success: true };
    },

    // =====================================================================
    // State Query
    // =====================================================================

    // Internal setters — called by React contexts to register live data
    _setAuthData(user, token) {
      _authData = { user, token: token ? String(token).substring(0, 10) + '...' : null };
    },
    _setNotificationData(unreadCount) {
      _notificationData = { unreadCount };
    },
    _setCompanyData(company) {
      _companyData = company;
    },

    /**
     * מחזיר state כללי של הדף
     */
    getState() {
      // Basic state
      const state = {
        path: window.location.pathname,
        search: window.location.search,
        title: document.title,
        dialogCount: document.querySelectorAll('[role="dialog"]').length,
        alertCount: document.querySelectorAll('[role="alert"]').length,
        loadingIndicators: document.querySelectorAll('[role="progressbar"], .MuiCircularProgress-root').length,
      };

      // Auth state — from registered context data
      state.isAuthenticated = !!_authData.user;
      state.user = _authData.user;
      state.tokenPreview = _authData.token;
      state.hasDefaultPasswordWarning = !!document.querySelector('[data-testid="layout__alert--default-password"]');

      // Company data
      state.company = _companyData;

      // Notifications
      state.unreadCount = _notificationData.unreadCount;

      // Dashboard data indicators
      state.dashboardDataLoaded = {
        summary: !!document.querySelector('[data-testid="dashboard__card--companyScore"]'),
        unitScores: !!document.querySelector('[data-testid="dashboard__chart--unit-scores"]'),
        categoryBreakdown: !!document.querySelector('[data-testid="dashboard__chart--category-breakdown"]'),
        lowScores: !!document.querySelector('[data-testid="dashboard__panel--low-scores"]'),
        alertsSummary: !!document.querySelector('[data-testid="dashboard__panel--alerts"]'),
        breadcrumb: !!document.querySelector('[data-testid="dashboard__breadcrumb"]'),
      };

      // Visible testids — snapshot of all testids in DOM
      state.visibleTestIds = Array.from(document.querySelectorAll('[data-testid]'))
        .map((el) => el.dataset.testid)
        .filter((tid) => {
          const el = document.querySelector(`[data-testid="${tid}"]`);
          return el && el.offsetParent !== null;
        });

      // API log count
      state.apiLogCount = getApiLog().length;
      state.lastApiCall = getLastApiCall();

      return state;
    },

    /**
     * בודק אם דיאלוג פתוח
     */
    isDialogOpen() {
      return document.querySelectorAll('[role="dialog"]').length > 0;
    },

    /**
     * מחזיר תוכן הדיאלוג הפתוח
     */
    getDialogContent() {
      const dialog = document.querySelector('[role="dialog"]');
      if (!dialog) return null;
      return {
        title: dialog.querySelector('.MuiDialogTitle-root')?.textContent?.trim() || '',
        content: dialog.querySelector('.MuiDialogContent-root')?.textContent?.trim() || '',
        actions: Array.from(dialog.querySelectorAll('.MuiDialogActions-root button')).map(
          (btn) => ({
            text: btn.textContent?.trim(),
            testId: btn.dataset.testid || null,
            disabled: btn.disabled,
          })
        ),
      };
    },

    /**
     * מחזיר נתוני טבלה
     * @param {string} tableTestId — data-testid של הטבלה
     * @returns {{ headers: string[], rows: string[][], rowCount: number }}
     */
    getTableData(tableTestId) {
      const table = _findByTestId(tableTestId);
      if (!table) return null;

      const headers = Array.from(table.querySelectorAll('thead th')).map(
        (th) => th.textContent?.trim()
      );
      const rows = Array.from(table.querySelectorAll('tbody tr')).map((tr) =>
        Array.from(tr.querySelectorAll('td')).map((td) => td.textContent?.trim())
      );

      return { headers, rows, rowCount: rows.length };
    },

    /**
     * מחזיר מספר שורות בטבלה
     */
    getTableRowCount(tableTestId) {
      const table = _findByTestId(tableTestId);
      if (!table) return 0;
      return table.querySelectorAll('tbody tr').length;
    },

    /**
     * מחזיר מידע על אלמנט
     * @param {string} testId
     */
    getElement(testId) {
      const el = _findByTestId(testId);
      if (!el) return null;
      return {
        tag: el.tagName.toLowerCase(),
        text: el.textContent?.trim(),
        value: el.value,
        visible: el.offsetParent !== null,
        disabled: el.disabled || el.getAttribute('aria-disabled') === 'true',
        className: el.className,
        testId,
      };
    },

    /**
     * מחזיר טקסט של אלמנט
     */
    getElementText(testId) {
      const el = _findByTestId(testId);
      return el ? el.textContent?.trim() : null;
    },

    /**
     * מחזיר ערך של input/select
     */
    getElementValue(testId) {
      const el = _findByTestId(testId);
      if (!el) return null;
      const input = el.querySelector('input, textarea, select') || el;
      return input.value ?? null;
    },

    /**
     * בודק אם אלמנט נראה
     */
    isElementVisible(testId) {
      const el = _findByTestId(testId);
      if (!el) return false;
      const style = getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') return false;
      // position:fixed elements have offsetParent === null — check them separately
      if (style.position === 'fixed') return true;
      // MUI Popover: the testid might be on a wrapper while content is in a portal
      // Check if element or its paper child has dimensions
      if (el.offsetParent === null) {
        // Could be a MUI portal — check children for visible content
        const paper = el.querySelector('.MuiPaper-root, .MuiPopover-paper');
        if (paper && paper.offsetHeight > 0) return true;
        return false;
      }
      return true;
    },

    /**
     * בודק אם אלמנט disabled
     */
    isElementDisabled(testId) {
      const el = _findByTestId(testId);
      if (!el) return null; // null = לא נמצא
      return el.disabled || el.getAttribute('aria-disabled') === 'true' ||
        el.classList.contains('Mui-disabled');
    },

    // =====================================================================
    // Wait
    // =====================================================================

    /**
     * מחכה שאלמנט יופיע ב-DOM
     * @param {string} testId
     * @param {number} timeout — ms
     */
    waitForElement(testId, timeout = 5000) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const check = () => {
          const el = _findByTestId(testId);
          if (el && el.offsetParent !== null) {
            resolve({
              success: true,
              element: {
                tag: el.tagName.toLowerCase(),
                text: el.textContent?.trim(),
                testId,
              },
            });
            return;
          }
          if (Date.now() - startTime > timeout) {
            reject(new Error(`[TH-460] waitForElement: timeout (${timeout}ms) waiting for [data-testid="${testId}"]`));
            return;
          }
          setTimeout(check, 100);
        };
        check();
      });
    },

    /**
     * מחכה שהנתיב ישתנה לערך מסוים
     * @param {string} expectedPath
     * @param {number} timeout — ms
     */
    waitForNavigation(expectedPath, timeout = 5000) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const check = () => {
          const currentPath = window.location.pathname;
          if (currentPath === expectedPath || currentPath.startsWith(expectedPath)) {
            resolve({ success: true, path: currentPath });
            return;
          }
          if (Date.now() - startTime > timeout) {
            reject(new Error(
              `[TH-310] waitForNavigation: timeout (${timeout}ms) waiting for ${expectedPath}, current: ${currentPath}`
            ));
            return;
          }
          setTimeout(check, 100);
        };
        check();
      });
    },

    // =====================================================================
    // Shortcuts — פעולות נפוצות בקצרה
    // =====================================================================

    /**
     * login מקצר: ממלא ושולח טופס login
     * @param {string} username
     * @param {string} password
     * @param {string|null} password2 — לsuper admin
     * @returns {{ path, lastApiCall }}
     */
    async login(username, password, password2 = null) {
      console.log('[TH] login:', username, password2 ? '(with 2FA)' : '(standard)');

      // =================================================================
      // SA login (2FA) — direct API call via AuthContext.login()
      // Bypasses the 2-step UI flow entirely for reliability
      // =================================================================
      if (password2 && window.__MADAD_TEST__._authLogin) {
        console.log('[TH] login: using direct _authLogin for SA (2FA)');
        try {
          const authResult = await window.__MADAD_TEST__._authLogin(username, password, password2);
          console.log('[TH] login: _authLogin result:', authResult);

          if (authResult?.success) {
            // AuthContext updated state — wait for React to re-render
            await _tick(300);
            // Navigate to dashboard if still on login page
            if (window.location.pathname === '/login' && _navigateRef) {
              _navigateRef('/dashboard');
              await _tick(300);
            }
          }

          const result = {
            success: authResult?.success === true && window.location.pathname !== '/login',
            path: window.location.pathname,
            lastApiCall: getLastApiCall(),
            apiLog: getApiLog().filter((e) => e.url?.includes('/auth/login')),
          };
          console.log('[TH] login result:', result.success, result.path);
          return result;
        } catch (err) {
          console.error('[TH] login: _authLogin failed:', err.message);
          return {
            ...(_err(603, 'login', `_authLogin exception: ${err.message}`)),
            path: window.location.pathname,
            lastApiCall: getLastApiCall(),
          };
        }
      }

      // =================================================================
      // Regular login (no 2FA) — UI flow via fillForm + submit
      // =================================================================
      await window.__MADAD_TEST__.fillForm({
        'login__input--username': username,
        'login__input--password': password,
      });
      await _tick(100);

      await window.__MADAD_TEST__.clickButton('login__button--submit');

      try {
        const apiResult = await waitForApiCall('/auth/login', 5000);
        console.log('[TH] login: API response status:', apiResult?.status);

        if (apiResult?.status === 403 && password2) {
          // Unexpected 2FA on non-SA login — fallback to direct auth
          console.log('[TH] login: unexpected 403, falling back to _authLogin');
          if (window.__MADAD_TEST__._authLogin) {
            const authResult = await window.__MADAD_TEST__._authLogin(username, password, password2);
            await _tick(300);
            if (authResult?.success && window.location.pathname === '/login' && _navigateRef) {
              _navigateRef('/dashboard');
              await _tick(300);
            }
          }
        } else {
          // Non-2FA: wait for navigation
          await _tick(500);
        }
      } catch {
        // waitForApiCall timeout — maybe login already completed
        await _tick(500);
      }

      const result = {
        success: window.location.pathname !== '/login',
        path: window.location.pathname,
        lastApiCall: getLastApiCall(),
        apiLog: getApiLog().filter((e) => e.url?.includes('/auth/login')),
      };
      console.log('[TH] login result:', result.success, result.path);
      return result;
    },

    /**
     * logout מקצר
     */
    async logout() {
      console.log('[TH] logout: starting, current path:', window.location.pathname);

      const avatarBtn = _findByTestId('layout__button--user-menu');
      if (!avatarBtn) {
        // User might be on login page (not authenticated)
        if (window.location.pathname.includes('/login')) {
          console.log('[TH] logout: already on login page — not authenticated');
          return { success: true, path: window.location.pathname, note: 'already on login page' };
        }
        return _err(610, 'logout', 'user-menu button not found (not on authenticated page?)');
      }

      avatarBtn.click();
      await _tick(300);

      const logoutBtn = _findByTestId('layout__menu-item--logout');
      if (!logoutBtn) {
        return _err(610, 'logout', 'logout menu item not found after opening user menu');
      }

      logoutBtn.click();
      await _tick(500);
      return { success: true, path: window.location.pathname };
    },

    /**
     * ניווט + המתנה לטעינה
     * @param {string} path
     * @param {string|null} waitForTestId — אלמנט שצריך להופיע לפני return
     * @param {number} timeout
     */
    async navigateAndWait(path, waitForTestId = null, timeout = 5000) {
      if (_navigateRef) {
        _navigateRef(path);
      }
      await _tick(100);

      if (waitForTestId) {
        await window.__MADAD_TEST__.waitForElement(waitForTestId, timeout);
      } else {
        await window.__MADAD_TEST__.waitForNavigation(path, timeout);
      }

      return {
        success: true,
        path: window.location.pathname,
      };
    },
  };

  // סימון שה-test harness פעיל + visual banner
  console.log('[TH] Test harness initialized. Access via window.__MADAD_TEST__');

  // Debug banner
  if (typeof document !== 'undefined') {
    const banner = document.createElement('div');
    banner.style.cssText = 'position:fixed;top:0;left:0;right:0;background:#ff6b00;color:#fff;text-align:center;padding:4px;font-size:12px;z-index:99999;font-family:monospace;';
    banner.textContent = '🔧 TEST MODE — Debug Active | window.__MADAD_TEST__';
    banner.id = 'madad-test-banner';
    const addBanner = () => {
      if (document.body && !document.getElementById('madad-test-banner')) {
        document.body.prepend(banner);
      }
    };
    if (document.body) addBanner();
    else document.addEventListener('DOMContentLoaded', addBanner);
  }
}

// =============================================================================
// AUTO-INIT: Run on module import.
// Supports 3 activation methods:
// 1. REACT_APP_TEST_MODE=true in .env (baked at build time)
// 2. ?test_mode=true in URL (runtime fallback)
// 3. Manual: setupTestHarness() call from App.js
// =============================================================================
const isTestMode =
  process.env.REACT_APP_TEST_MODE === 'true' ||
  (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('test_mode') === 'true');

if (isTestMode) {
  setupTestHarness();
}
